import React, { useMemo } from "react";

import { format } from "date-fns";
import { useTranslation } from "react-i18next";

import { useAppState } from "../../../../../context/StateContext";
import { Booking } from "../../../../../models/booking";
import { CommercialInvoice } from "../../../../../models/shipments";
import {
  CommercialInvoice as CommercialInvoiceField,
  CountryCode,
} from "../../../../../util/enums";
import { FieldViewProps } from "../../../../Form/FieldView";
import { getDateValueInTimeZone } from "../../../../Generic/MilestoneDates";
import { SectionView } from "../../../../Generic/SideView/SectionView";
import { useBookingResourcesContext } from "../../../hooks/useResourcesContext";

type BookingViewCommercialInvoiceProps = {
  commercialInvoice: CommercialInvoice | undefined;
  booking: Booking | undefined;
};
export const BookingViewCommercialInvoice: React.FC<BookingViewCommercialInvoiceProps> = (
  props
) => {
  const { commercialInvoice, booking } = props;

  const { t: translate } = useTranslation();
  const { isFMorSPVersion } = useAppState();
  const { countryList, portList } = useBookingResourcesContext();
  const countrySelected = countryList?.find(
    (country) => country?.id === commercialInvoice?.countryId
  );
  const baseViewFieldProps = (key: keyof CommercialInvoice) => {
    const props: FieldViewProps = {
      label: translate(key),
    };

    return props;
  };

  const isLoadingPortFromBangladesh = useMemo(() => {
    let countryCode;

    if (isFMorSPVersion) countryCode = booking?.portOfLoading?.country?.code;
    else
      countryCode =
        portList.find((port) => port.id === booking?.portOfLoadingId)?.country?.code ??
        "countryCode";

    return countryCode === CountryCode.BANGLADESH;
  }, [booking?.portOfLoadingId, portList]);

  const bangladeshFields = () => {
    if (isLoadingPortFromBangladesh)
      return [
        [
          {
            ...baseViewFieldProps(CommercialInvoiceField.BANK_NAME),
            label: translate(CommercialInvoiceField.BANK_NAME),
            value: commercialInvoice?.[CommercialInvoiceField.BANK_NAME],
          },
          {
            ...baseViewFieldProps(CommercialInvoiceField.BANK_ADDRESS),
            label: translate(CommercialInvoiceField.BANK_ADDRESS),
            value: commercialInvoice?.[CommercialInvoiceField.BANK_ADDRESS]?.longAddress,
          },
          {
            ...baseViewFieldProps(CommercialInvoiceField.ACCOUNT_NUMBER),
            label: translate(CommercialInvoiceField.ACCOUNT_NUMBER),
            value: commercialInvoice?.[CommercialInvoiceField.ACCOUNT_NUMBER],
          },
          {
            ...baseViewFieldProps(CommercialInvoiceField.SWIFT_CODE),
            label: translate(CommercialInvoiceField.SWIFT_CODE),
            value: commercialInvoice?.[CommercialInvoiceField.SWIFT_CODE],
          },
        ],
        [
          {
            ...baseViewFieldProps(CommercialInvoiceField.TERMS_OF_DELIVERY),
            label: translate(CommercialInvoiceField.TERMS_OF_DELIVERY),
            value: commercialInvoice?.[CommercialInvoiceField.TERMS_OF_DELIVERY],
          },
          {
            ...baseViewFieldProps(CommercialInvoiceField.UD_NUMBER),
            label: translate(CommercialInvoiceField.UD_NUMBER),
            value: commercialInvoice?.[CommercialInvoiceField.UD_NUMBER],
          },
          {
            ...baseViewFieldProps(CommercialInvoiceField.ERC_NUMBER),
            label: translate(CommercialInvoiceField.ERC_NUMBER),
            value: commercialInvoice?.[CommercialInvoiceField.ERC_NUMBER],
          },
          {
            ...baseViewFieldProps(CommercialInvoiceField.REX_NUMBER),
            label: translate(CommercialInvoiceField.REX_NUMBER),
            value: commercialInvoice?.[CommercialInvoiceField.REX_NUMBER],
          },
        ],
      ];

    return [];
  };

  const generalFields = [
    [
      {
        ...baseViewFieldProps(CommercialInvoiceField.INVOICE_NUMBER),
        label: `${translate("invoice")} #`,
        value: commercialInvoice?.[CommercialInvoiceField.INVOICE_NUMBER],
      },
      {
        ...baseViewFieldProps(CommercialInvoiceField.BENEFICIARY_NAME),
        label: translate(CommercialInvoiceField.BENEFICIARY_NAME),
        value: commercialInvoice?.[CommercialInvoiceField.BENEFICIARY_NAME],
      },
      {
        ...baseViewFieldProps(CommercialInvoiceField.BENEFICIARY_ADDRESS),
        label: translate(CommercialInvoiceField.BENEFICIARY_ADDRESS),
        value: commercialInvoice?.[CommercialInvoiceField.BENEFICIARY_ADDRESS]?.longAddress,
      },
    ],
    [
      {
        ...baseViewFieldProps(CommercialInvoiceField.VENDOR_ID),
        label: translate(CommercialInvoiceField.VENDOR_ID),
        value: commercialInvoice?.[CommercialInvoiceField.VENDOR_ID],
      },
      {
        ...baseViewFieldProps(CommercialInvoiceField.PAYMENT_TERMS),
        label: translate(CommercialInvoiceField.PAYMENT_TERMS),
        value: commercialInvoice?.[CommercialInvoiceField.PAYMENT_TERMS],
      },
      {
        ...baseViewFieldProps(CommercialInvoiceField.FISCAL_AGENT_DETAILS),
        label: translate(CommercialInvoiceField.FISCAL_AGENT_DETAILS),
        value: commercialInvoice?.[CommercialInvoiceField.FISCAL_AGENT_DETAILS],
      },
      {
        ...baseViewFieldProps(CommercialInvoiceField.COUNTRY_ID),
        label: translate("countryOfOrigin"),
        value: countrySelected?.name,
      },
    ],
    [
      {
        ...baseViewFieldProps(CommercialInvoiceField.SALES_CONTRACT_NUMBER),
        label: translate(CommercialInvoiceField.SALES_CONTRACT_NUMBER),
        value: commercialInvoice?.[CommercialInvoiceField.SALES_CONTRACT_NUMBER],
      },
      {
        ...baseViewFieldProps(CommercialInvoiceField.SALES_CONTRACT_DATE),
        label: translate(CommercialInvoiceField.SALES_CONTRACT_DATE),
        value: commercialInvoice?.[CommercialInvoiceField.SALES_CONTRACT_DATE] ? format(
          new Date(
            String(
              getDateValueInTimeZone(
                commercialInvoice?.[CommercialInvoiceField.SALES_CONTRACT_DATE]
              )
            )
          ),
          "dd/MM/yyyy"
        ) : "",
      },
      {
        ...baseViewFieldProps(CommercialInvoiceField.EXP_NUMBER),
        label: translate(CommercialInvoiceField.EXP_NUMBER),
        value: commercialInvoice?.[CommercialInvoiceField.EXP_NUMBER],
      },
      {
        ...baseViewFieldProps(CommercialInvoiceField.EXP_DATE),
        label: translate(CommercialInvoiceField.EXP_DATE),
        value: commercialInvoice?.[CommercialInvoiceField.EXP_DATE] ? format(
          new Date(
            String(getDateValueInTimeZone(commercialInvoice?.[CommercialInvoiceField.EXP_DATE]))
          ),
          "dd/MM/yyyy"
        ) : "",
      },
    ],
    ...bangladeshFields(),
  ];

  const exporterFields = [
    [
      {
        ...baseViewFieldProps(CommercialInvoiceField.EXPORTER_NAME),
        label: translate(CommercialInvoiceField.EXPORTER_NAME),
        value: commercialInvoice?.[CommercialInvoiceField.EXPORTER_NAME],
      },
      {
        ...baseViewFieldProps(CommercialInvoiceField.EXPORTER_CONTACT_DETAILS),
        label: translate(CommercialInvoiceField.EXPORTER_CONTACT_DETAILS),
        value: commercialInvoice?.[CommercialInvoiceField.EXPORTER_CONTACT_DETAILS],
      },
    ],
  ];

  return (
    <div>
      <SectionView fields={generalFields} title={translate("commercialInvoiceDetails")} />
      {commercialInvoice?.[CommercialInvoiceField.EXPORTER_NAME] ||
      commercialInvoice?.[CommercialInvoiceField.EXPORTER_CONTACT_DETAILS] ? (
        <SectionView fields={exporterFields} title={translate("exporter")} />
      ) : null}
    </div>
  );
};
